import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CurrencyInput from "react-currency-input-field";
import CurrencyConverter from "../../Currency/CurrencyConverter";
import { ApiHook } from "../../hooks/apiHook";
import { reverseNumberDisplay } from "../../utils/currencyNumberDisplay";
import { Modal, Form, Button, Spinner, ProgressBar } from "react-bootstrap";
const WithdrawGGFC = ({
  balance,
  show,
  handleClose,
  currency,
  minWithdrawTokenAmount,
  withdrawalFee,
  walletWithdrawalInfo,
}) => {
  const { t } = useTranslation();
  const [withdrawalMode, setWithdrawalMode] = useState("ggfc"); // "ggfc" or "wallet"
  const [withdrawData, setWithdrawData] = useState({
    amount: "",
    transactionPassword: "",
  });


  console.log(withdrawData);
  
  const conversionFactor = useSelector(
    (state) => state?.user?.conversionFactor
  );

  const defaultCurrencyJson = useSelector(
    (state) => state?.user?.loginResponse?.defaultCurrency
  );

  const defaultCurrency = defaultCurrencyJson
    ? JSON.parse(defaultCurrencyJson)
    : null;

  const [errorMessage, setErrorMessage] = useState({
    amount: null,
    transactionPassword: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize the withdrawal mutations
  const withdrawGGFCMutation = ApiHook.CallWithdrawGGFC();
  const withdrawWalletMutation = ApiHook.CallWithdrawWalletToken(); // Add this API hook

  const handleChange = (e) => {
    const { id, value } = e.target;
    setWithdrawData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
    setErrorMessage((prevData) => ({
      ...prevData,
      [id]: null,
    }));
  };

  const handleAmountChange = (value) => {
    setWithdrawData((prevData) => ({
      ...prevData,
      amount: value,
    }));
    setErrorMessage((prevData) => ({
      ...prevData,
      amount: null,
    }));
  };

  const handleModeSwitch = (mode) => {
    setWithdrawalMode(mode);
    if(mode === "wallet") {
      // Reset amount for wallet mode as it's fixed
      setWithdrawData((prevData) => ({
        ...prevData,
        amount: walletWithdrawalInfo?.withdrawableAmount || 0,
      }));
      setErrorMessage((prevData) => ({
        ...prevData,
        amount: null,
      }));
    } else {
      // Reset all fields for GGFC mode 
    setWithdrawData({
      amount: "",
      transactionPassword: "",
    });
    setErrorMessage({
      amount: null,
      transactionPassword: null,
    });
  };
}

  const getMaxWithdrawableAmount = () => {
    if (withdrawalMode === "wallet" && walletWithdrawalInfo) {
      return CurrencyConverter(
        walletWithdrawalInfo?.remainingWithdrawable,
        conversionFactor
      );
    }
    return CurrencyConverter(balance, conversionFactor);
  };

  const isFormValid = () => {
    if (withdrawalMode === "wallet") {
      // For wallet mode, only password is required
      return (
        withdrawData?.transactionPassword.trim() !== "" &&
        walletWithdrawalInfo?.canWithdraw &&withdrawData?.amount > 0 
      );
    }
    // For GGFC mode, both amount and password are required
    return (
      withdrawData?.amount > 0 &&
      withdrawData?.transactionPassword.trim() !== ""
    );
  };

  const validateForm = () => {
    let isValid = true;
    const errors = {
      amount: null,
      transactionPassword: null,
    };

    // For wallet mode, use fixed 10% amount
    if (withdrawalMode === "wallet") {
      const walletAmount = parseFloat(
        walletWithdrawalInfo?.withdrawableAmount || 0
      );

      if (walletAmount <= 0) {
        errors.amount = "Invalid withdrawal amount";
        isValid = false;
      } else if (!walletWithdrawalInfo?.canWithdraw) {
        if (!walletWithdrawalInfo?.isUnlocked) {
          errors.amount = `Tokens are locked until ${new Date(
            walletWithdrawalInfo?.unlockDate
          ).toLocaleDateString()}`;
        } else if (walletWithdrawalInfo?.remainingWithdrawable <= 0) {
          errors.amount = "No remaining withdrawable amount";
        }
        isValid = false;
      }
    } else {
      // For GGFC mode, validate entered amount
      const minAmount = Number(minWithdrawTokenAmount);
      const maxAmount = parseFloat(balance);

      if (!withdrawData.amount || parseFloat(withdrawData.amount) < minAmount) {
        errors.amount = `Amount must be greater than ${minAmount.toFixed(
          2
        )} GGFC`;
        isValid = false;
      }

      if (parseFloat(withdrawData.amount) > maxAmount) {
        errors.amount = "Insufficient balance";
        isValid = false;
      }
    }

    if (!withdrawData.transactionPassword.trim()) {
      errors.transactionPassword = "Transaction password is required";
      isValid = false;
    }

    setErrorMessage(errors);
    return isValid;
  };

  const calculateWithdrawalPercentage = () => {
    if (walletWithdrawalInfo && walletWithdrawalInfo?.totalTokens > 0) {
      const withdrawn = walletWithdrawalInfo?.withdrawnAmount || 0;
      const total = walletWithdrawalInfo?.totalTokens;
      return ((withdrawn / total) * 100).toFixed(2);
    }
    return "0";
  };

  const calculateFeeAmount = () => {
    const amount =
      withdrawalMode === "wallet"
        ? Number(walletWithdrawalInfo?.withdrawableAmount || 0)
        : parseFloat(withdrawData.amount || 0);

    const feePercentage = parseFloat(withdrawalFee) || 0;
    const feeAmount = (amount * feePercentage) / 100;
    return feeAmount.toFixed(2);
  };

  const calculateNetAmount = () => {
    const amount =
      withdrawalMode === "wallet"
        ? Number(walletWithdrawalInfo?.withdrawableAmount || 0)
        : parseFloat(withdrawData.amount || 0);

    const feePercentage = parseFloat(withdrawalFee) || 0;
    const feeAmount = (amount * feePercentage) / 100;
    const netAmount = amount - feeAmount;
    return Math.max(netAmount, 0).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // For wallet mode, use fixed 10% amount
      const amountToWithdraw = withdrawData.amount;

      let convertAmount;
      if (currency?.id === defaultCurrency?.id) {
        convertAmount = reverseNumberDisplay(
          CurrencyConverter(amountToWithdraw, conversionFactor, 0)
        );
      } else {
        convertAmount = reverseNumberDisplay(
          CurrencyConverter(amountToWithdraw, conversionFactor, 1)
        );
      }

      const withdrawalData = {
        amount: convertAmount,
        transactionPassword: withdrawData.transactionPassword,
        tokenType: "GGFC",
      };

      // Use different API based on withdrawal mode
      const mutation =
        withdrawalMode === "wallet"
          ? withdrawWalletMutation
          : withdrawGGFCMutation;

      await mutation.mutateAsync(withdrawalData, {
        onSuccess: (res) => {
          if (res.status === 200) {
            setWithdrawalMode("ggfc");
            setWithdrawData({
              amount: "",
              transactionPassword: "",
            });
            setErrorMessage({
              amount: null,
              transactionPassword: null,
            });
            handleClose();
          }
        },
        onError: (error) => {
          console.error("Withdrawal submission error:", error);
        },
      });
    } catch (error) {
      console.error("Withdrawal error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setWithdrawData({
      amount: "",
      transactionPassword: "",
    });
    setErrorMessage({
      amount: null,
      transactionPassword: null,
    });
    setWithdrawalMode("ggfc");
    handleClose();
  };
  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      size="xl"
      centered
      className="withdraw-modal"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center">
            <div className="modal-icon me-3">
              <i className="fas fa-arrow-down text-white"></i>
            </div>
            <div>
              <h4 className="mb-0">
                {withdrawalMode === "wallet"
                  ? "Withdraw Wallet Tokens"
                  : t("withdraw_ggfc")}
              </h4>
              <small className="text-muted">
                {withdrawalMode === "wallet"
                  ? ""
                  : "Withdraw your GGFC tokens securely"}
              </small>
            </div>
          </div>
          {walletWithdrawalInfo?.canWithdraw && (
            <div className="me-3">
              <Button
                variant={withdrawalMode === "wallet" ? "primary" : "success"}
                onClick={() =>
                  handleModeSwitch(
                    withdrawalMode === "wallet" ? "ggfc" : "wallet"
                  )
                }
              >
                {withdrawalMode === "wallet"
                  ? "Withdraw GGFC"
                  : "Withdraw Wallet Tokens"}
              </Button>
            </div>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {withdrawalMode === "wallet" ? (
          <div
            className="alert alert-warning border-0 mb-4"
            style={{ backgroundColor: "#fff3cd" }}
          >
            <div className="d-flex align-items-center">
              <i className="fas fa-calendar-alt text-warning me-2"></i>
              <div>
                <strong>Monthly Withdrawal Limit</strong>
                <div className="small">
                  You can withdraw up to 10% of your Wallet Token ({" "}
                  {walletWithdrawalInfo?.withdrawableAmount || 0} GGFC) per
                  month starting from this month onwards.
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="alert alert-info border-0 mb-4"
            style={{ backgroundColor: "#e8f4fd" }}
          >
            <div className="d-flex align-items-center">
              <i className="fas fa-info-circle text-primary me-2"></i>
              <div>
                <strong>Secure Withdrawal</strong>
                <div className="small">
                  Your GGFC tokens will be processed securely.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row g-4">
          {/* Left Side - Withdrawal Form */}
          <div className="col-lg-7">
            <div className="withdrawal-form-container">
              <div className="form-header mb-4">
                <h5 className="mb-2">Withdrawal Details</h5>
                {withdrawalMode === "ggfc" && (
                  <p className="text-muted small mb-0">
                    Enter the amount you want to withdraw and confirm with your
                    password
                  </p>
                )}
              </div>

              <Form onSubmit={handleSubmit}>
                {/* Available Balance Display */}
                {withdrawalMode === "ggfc" && (
                  <div className="balance-display mb-4">
                    <div className="card border-0 bg-light">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">
                              {"Available Balance"}
                            </small>
                            <div className="fw-bold text-success fs-5">
                              {withdrawalMode === "wallet"
                                ? `${parseFloat(
                                    Number(walletWithdrawalInfo?.totalTokens)
                                  ).toFixed(2)} GGFC`
                                : `${CurrencyConverter(
                                    balance,
                                    conversionFactor
                                  )} GGFC`}
                            </div>
                          </div>
                          <div className="balance-icon">
                            <i className="fas fa-wallet text-success"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Amount Input - Only for GGFC mode */}
                {withdrawalMode === "ggfc" && (
                  <div className="form-section mb-4">
                    <Form.Group>
                      <Form.Label className="form-label-custom">
                        <i className="fas fa-coins me-2 text-primary"></i>
                        {t("withdrawal_amount")}
                        <span className="text-danger ms-1">*</span>
                      </Form.Label>
                      <div className="amount-input-wrapper">
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0">
                            <i className="fas fa-arrow-down text-danger"></i>
                          </span>
                          <Form.Control
                            as="select"
                            disabled
                            className="token-selector"
                          >
                            <option value="">GGFC</option>
                          </Form.Control>
                          <CurrencyInput
                            name="amount"
                            id="amount"
                            className="form-control border-start-0"
                            placeholder="0.00"
                            value={withdrawData.amount}
                            onValueChange={(value) => handleAmountChange(value)}
                            required
                            allowNegativeValue={false}
                            decimalsLimit={4}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() =>
                              handleAmountChange(getMaxWithdrawableAmount())
                            }
                          >
                            Max
                          </button>
                        </div>
                        {errorMessage.amount && (
                          <div className="invalid-feedback d-block">
                            <i className="fas fa-exclamation-triangle me-1"></i>
                            {errorMessage.amount}
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  </div>
                )}

                {/* Fixed Amount Display for Wallet Token */}
                {withdrawalMode === "wallet" && walletWithdrawalInfo && (
                  <>
                    {/* Vesting Schedule Overview */}
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-header bg-gradient-primary text-white">
                        <h6 className="mb-0">
                          <i className="fas fa-chart-line me-2"></i>
                          Vesting Schedule Overview
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="vesting-stat-card">
                              <div className="d-flex align-items-center mb-2">
                                <div className="stat-icon bg-primary-light me-3">
                                  <i className="fas fa-coins text-primary"></i>
                                </div>
                                <div>
                                  <small className="text-muted d-block">
                                    Total Tokens
                                  </small>
                                  <h5 className="mb-0 fw-bold">
                                    {walletWithdrawalInfo?.totalTokens ?? 0}{" "}
                                    GGFC
                                  </h5>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="vesting-stat-card">
                              <div className="d-flex align-items-center mb-2">
                                <div className="stat-icon bg-success-light me-3">
                                  <i className="fas fa-unlock-alt text-success"></i>
                                </div>
                                <div>
                                  <small className="text-muted d-block">
                                    Withdrawable (10%)
                                  </small>
                                  <h5 className="mb-0 fw-bold text-success">
                                    {walletWithdrawalInfo &&
                                      Number(
                                        walletWithdrawalInfo?.withdrawableAmount
                                      ).toFixed(2)}{" "}
                                    GGFC
                                  </h5>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="vesting-stat-card">
                              <div className="d-flex align-items-center mb-2">
                                <div className="stat-icon bg-warning-light me-3">
                                  <i className="fas fa-arrow-down text-warning"></i>
                                </div>
                                <div>
                                  <small className="text-muted d-block">
                                    Already Withdrawn
                                  </small>
                                  <h5 className="mb-0 fw-bold text-warning">
                                    {Number(
                                      walletWithdrawalInfo?.withdrawnAmount ?? 0
                                    ).toFixed(2)}{" "}
                                    GGFC
                                  </h5>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="vesting-stat-card">
                              <div className="d-flex align-items-center mb-2">
                                <div className="stat-icon bg-info-light me-3">
                                  <i className="fas fa-wallet text-info"></i>
                                </div>
                                <div>
                                  <small className="text-muted d-block">
                                    Remaining Available
                                  </small>
                                  <h5 className="mb-0 fw-bold text-info">
                                    {walletWithdrawalInfo?.remainingWithdrawable.toFixed(
                                      2
                                    )}{" "}
                                    GGFC
                                  </h5>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Withdrawal Progress Bar */}
                        <div className="mt-4">
                          <div className="d-flex justify-content-between mb-2">
                            <small className="text-muted">
                              Withdrawal Progress
                            </small>
                            <small className="fw-bold">
                              {calculateWithdrawalPercentage()}%
                            </small>
                          </div>
                          <ProgressBar
                            now={parseFloat(calculateWithdrawalPercentage())}
                            variant={
                              parseFloat(calculateWithdrawalPercentage()) ===
                              100
                                ? "success"
                                : "primary"
                            }
                            style={{ height: "10px" }}
                          />
                          <small className="text-muted mt-1 d-block">
                            {Number(
                              walletWithdrawalInfo?.withdrawnAmount ?? 0
                            ).toFixed(2)}{" "}
                            of{" "}
                            {Number(
                              walletWithdrawalInfo?.totalTokens ?? 0
                            ).toFixed(2)}{" "}
                            GGFC withdrawn
                          </small>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Transaction Password */}
                <div className="form-section mb-4">
                  <Form.Group>
                    <Form.Label className="form-label-custom">
                      <i className="fas fa-lock me-2 text-primary"></i>
                      {t("transactionPassword")}
                      <span className="text-danger ms-1">*</span>
                    </Form.Label>
                    <Form.Control
                      type="password"
                      id="transactionPassword"
                      placeholder="Enter your transaction password"
                      value={withdrawData.transactionPassword}
                      onChange={handleChange}
                      isInvalid={errorMessage?.transactionPassword !== null}
                      className="form-control-custom"
                    />
                    <Form.Control.Feedback type="invalid">
                      <i className="fas fa-exclamation-triangle me-1"></i>
                      {errorMessage.transactionPassword}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      <i className="fas fa-shield-alt me-1"></i>
                      Your transaction password ensures secure withdrawals
                    </Form.Text>
                  </Form.Group>
                </div>

                {/* Submit Button */}
                <div className="submit-section mt-4">
                  <Button
                    variant="danger"
                    type="submit"
                    disabled={
                      !isFormValid() ||
                      isSubmitting ||
                      (withdrawalMode === "wallet"
                        ? withdrawWalletMutation.isLoading
                        : withdrawGGFCMutation.isLoading)
                    }
                    className="btn-withdraw w-100"
                    size="lg"
                  >
                    {isSubmitting ||
                    (withdrawalMode === "wallet"
                      ? withdrawWalletMutation.isLoading
                      : withdrawGGFCMutation.isLoading) ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>
                        Processing Withdrawal...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-2"></i>
                        Submit Withdrawal Request
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </div>
          </div>

          {/* Right Side - Summary and Information */}
          <div className="col-lg-5">
            <div className="withdrawal-preview">
              {/* Withdrawal Summary */}
              {withdrawData.amount && (
                <div className="withdrawal-summary-card mb-4">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-warning text-dark border-0">
                      <h6 className="mb-0 d-flex align-items-center">
                        <i className="fas fa-receipt me-2"></i>
                        Withdrawal Summary
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="summary-item">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="text-muted">Withdrawal Amount:</span>
                          <span className="fw-bold">
                            {withdrawData.amount} GGFC
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="text-muted">Withdrawal Fee ({Number(withdrawalFee).toFixed(2)}%):</span>
                          <span className="fw-bold text-danger">
                            {calculateFeeAmount()} GGFC
                          </span>
                        </div>
                        <hr className="my-3" />
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted fw-bold">
                            Net Amount:
                          </span>
                          <span className="fw-bold text-success fs-5">
                            {calculateNetAmount()} GGFC
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Withdrawal Information */}
              <div className="withdrawal-info-card">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-info text-white border-0">
                    <h6 className="mb-0 d-flex align-items-center">
                      <i className="fas fa-info-circle me-2"></i>
                      Withdrawal Information
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="info-grid">
                      {withdrawalMode === "ggfc" && (
                        <div className="info-item mb-3">
                          <div className="d-flex align-items-center">
                            <div className="info-icon me-3">
                              <i className="fas fa-coins text-primary"></i>
                            </div>
                            <div>
                              <div className="fw-semibold">
                                Minimum Withdrawal
                              </div>
                              <small className="text-muted">
                                {Number(minWithdrawTokenAmount).toFixed(2)} GGFC
                              </small>
                            </div>
                          </div>
                        </div>
                      )}

                      {withdrawalMode === "wallet" && (
                        <div className="info-item mb-3">
                          <div className="d-flex align-items-center">
                            <div className="info-icon me-3">
                              <i className="fas fa-percentage text-primary"></i>
                            </div>
                            <div>
                              <div className="fw-semibold">Monthly Limit</div>
                              <small className="text-muted">
                                10% of{" "}
                                {Number(
                                  walletWithdrawalInfo?.totalTokens
                                ).toFixed(2)}{" "}
                                GGFC
                              </small>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="info-item mb-3">
                        <div className="d-flex align-items-center">
                          <div className="info-icon me-3">
                            <i className="fas fa-money-bill-wave text-warning"></i>
                          </div>
                          <div>
                            <div className="fw-semibold">Withdrawal Fee</div>
                            <small className="text-muted">
                              {Number(withdrawalFee).toFixed(2)}% per
                              transaction
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="info-item mb-3">
                        <div className="d-flex align-items-center">
                          <div className="info-icon me-3">
                            <i className="fas fa-shield-alt text-success"></i>
                          </div>
                          <div>
                            <div className="fw-semibold">Security</div>
                            <small className="text-muted">
                              Password protected
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {withdrawalMode === "wallet" && (
                      <div className="mt-4 p-3 bg-light rounded">
                        <div className="alert alert-info border-0 mb-0 p-2">
                          <small>
                            <i className="fas fa-calendar-check me-1"></i>
                            <strong>Note:</strong> Wallet token withdrawals are
                            available from this month onwards with a 10% monthly
                            limit
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <style jsx>{`
        .withdraw-modal .modal-content {
          border: none;
          border-radius: 16px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        }

        .modal-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }

        .withdrawal-form-container {
          background: #fff;
          border-radius: 12px;
          padding: 0;
        }

        .form-header h5 {
          color: #2c3e50;
          font-weight: 600;
        }

        .balance-display .card {
          border: 2px solid #e8f5e8;
          background: linear-gradient(135deg, #f8fff8 0%, #e8f5e8 100%);
        }

        .balance-icon {
          width: 40px;
          height: 40px;
          background: rgba(40, 167, 69, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }

        .form-label-custom {
          font-weight: 600;
          color: #495057;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .amount-input-wrapper .input-group {
          border-radius: 8px;
          overflow: hidden;
          border: 2px solid #e9ecef;
          transition: border-color 0.3s ease;
        }

        .amount-input-wrapper .input-group:focus-within {
          border-color: #dc3545;
          box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
        }

        .token-selector {
          max-width: 80px;
          border: none;
          background: #f8f9fa;
          font-weight: 600;
          color: #dc3545;
        }

        .form-control-custom {
          border: 2px solid #e9ecef;
          border-radius: 8px;
          padding: 12px 16px;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .form-control-custom:focus {
          border-color: #dc3545;
          box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
        }
        .bg-gradient-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        .btn-withdraw {
          background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
          border: none;
          border-radius: 12px;
          padding: 16px 24px;
          font-weight: 600;
          letter-spacing: 0.5px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
        }

        .btn-withdraw:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(220, 53, 69, 0.4);
        }

        .btn-withdraw:disabled {
          opacity: 0.6;
          transform: none;
        }

        .withdrawal-summary-card .card-header {
          background: linear-gradient(
            135deg,
            #ffc107 0%,
            #fd7e14 100%
          ) !important;
        }

        .withdrawal-info-card .card-header {
          background: linear-gradient(
            135deg,
            #17a2b8 0%,
            #138496 100%
          ) !important;
        }

        .info-icon {
          width: 32px;
          height: 32px;
          background: rgba(0, 123, 255, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
        }

        .info-grid {
          font-size: 14px;
        }

        .invalid-feedback {
          font-size: 13px;
          color: #dc3545;
          margin-top: 6px;
        }

        .alert {
          border-radius: 8px;
        }

        @media (max-width: 768px) {
          .withdraw-modal .modal-dialog {
            margin: 10px;
          }

          .withdrawal-form-container,
          .withdrawal-preview {
            padding: 20px;
          }
        }
      `}</style>
    </Modal>
  );
};

export default WithdrawGGFC;
